import { createClient, AgentEvents } from '@deepgram/sdk';
import { Buffer } from 'buffer';

window.Buffer = Buffer;

const AiAgent = {
    connection: null,
    candidateName: '',
    companyName: '',
    questions: [],
    additionalInstructions: '',
    // ── Callbacks (set by orchestrator) ──────────────────────────────────────
    onReady: null,
    onAudioChunk: null,
    onTranscript: null,
    onQuestionComplete: null,
    onSpeakingChange: null,
    onOpenCodeEditor: null,
    onCurrentQuestion: null,   // (id: string) => void
    onInterviewEnd: null,      // () => void

    // ── Speech timestamps ─────────────────────────────────────────────────
    _userSpeechStart: null,
    _userSpeechEnd: null,
    _agentSpeechStart: null,
    _agentSpeechEnd: null,

    // ── Public API ────────────────────────────────────────────────────────────
    async initialize() {
        console.log('🚀 AiAgent initializing...');
        await this._connect();
        console.log('✅ AiAgent ready');
        if (typeof this.onReady === 'function') this.onReady();
    },

    _stripHtml(html) {
        return html?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() ?? ''
    },

    reset() {
        if (this.connection) {
            try { this.connection.disconnect(); } catch (_) { }
            this.connection = null;
        }
        console.log('🔄 AiAgent reset');
    },

    stop() {
        this.reset();
    },

    sendAudioChunk(pcmBuffer) {
        if (this.connection) {
            this.connection.send(pcmBuffer);
        }
    },

    /**
     * Injects a context message into the live agent session mid-conversation.
     * Used by the nudge system to pass the candidate's current code so the
     * agent can give targeted, non-revealing hints like a real interviewer
     * glancing at the screen.
     *
     * Deepgram's sendUserText injects text as a user turn without
     * requiring an audio utterance from the candidate.
     */
    injectMessage(text) {
        if (!this.connection) {
            console.warn('⚠️ [AiAgent] Cannot inject — no active connection');
            return;
        }
        console.log('💉 [AiAgent] Injecting context message into conversation');
        this.connection.send(JSON.stringify({
            type: 'InjectUserMessage',
            content: text,
        }));
    },

    // ── Internal ──────────────────────────────────────────────────────────────

    async _connect() {
        let apiKey;

        try {
            const res = await fetch('https://hire100x-e1dd4.uc.r.appspot.com/identity/deepgram-token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json();
            apiKey = data.token;
            console.log('✅ [Deepgram] Fetched short-lived token from server');
        } catch (err) {
            console.error('❌ [Deepgram] Failed to fetch token:', err);
            throw new Error('Could not retrieve Deepgram token');
        }

        return new Promise((resolve, reject) => {
            const deepgram = createClient(apiKey);
            this.connection = deepgram.agent();

            this.connection.on(AgentEvents.Open, () => {
                console.log('✅ [Deepgram] Connection open');
                this.connection.configure(this._getSettings());
                resolve();
            });

            this.connection.on(AgentEvents.Welcome, (data) => {
                console.log('👋 [Deepgram] Welcome — request_id:', data.request_id);
            });

            this.connection.on(AgentEvents.SettingsApplied, () => {
                console.log('✅ [Deepgram] Settings applied');
            });

            this.connection.on(AgentEvents.UserStartedSpeaking, () => {
                this._userSpeechStart = Date.now();
                this._userSpeechEnd = null;
                console.log('🎙️ [TEST] User Started speaking at', this._userSpeechStart);
            });

            this.connection.on(AgentEvents.ConversationText, (data) => {
                console.log('🎙️ [TEST] User Ended speaking at', Date.now());
                console.log(`💬 [Transcript] ${data.role}: ${data.content}`);

                let timestamps;
                if (data.role === 'user') {
                    this._userSpeechEnd = Date.now();
                    timestamps = {
                        startTs: this._userSpeechStart ?? null,
                        endTs: this._userSpeechEnd,
                    };
                } else {
                    timestamps = {
                        startTs: this._agentSpeechStart ?? null,
                        endTs: this._agentSpeechEnd ?? null,
                    };
                }

                if (typeof this.onTranscript === 'function') {
                    this.onTranscript(data.role, data.content, timestamps);
                }
            });

            this.connection.on(AgentEvents.AgentStartedSpeaking, (data) => {
                this._agentSpeechStart = Date.now();
                this._agentSpeechEnd = null;
                console.log('🔊 [Agent] Started speaking at', this._agentSpeechStart, '— latency:', data.total_latency, 'ms');
                if (typeof this.onSpeakingChange === 'function') this.onSpeakingChange(true);
            });

            this.connection.on(AgentEvents.AgentAudioDone, () => {
                this._agentSpeechEnd = Date.now();
                console.log('🔇 [Agent] Audio done at', this._agentSpeechEnd);
                if (typeof this.onSpeakingChange === 'function') this.onSpeakingChange(false);
            });

            this.connection.on(AgentEvents.Audio, (audio) => {
                if (typeof this.onAudioChunk === 'function') {
                    this.onAudioChunk(audio instanceof ArrayBuffer ? audio : audio.buffer);
                }
            });

            this.connection.on(AgentEvents.FunctionCallRequest, (data) => {
                console.log('📨 [FunctionCall] Received via FunctionCallRequest event:', JSON.stringify(data, null, 2));
                this._handleFunctionCall(data);
            });

            this.connection.on(AgentEvents.Error, (data) => {
                if (typeof data.data === 'string') {
                    try {
                        const parsed = JSON.parse(data.data);
                        if (parsed.type === 'FunctionCallRequest') {
                            console.warn('⚠️ [Deepgram] FunctionCallRequest via Error event — ignoring duplicate');
                            return;
                        }
                    } catch (_) { /* not JSON, fall through */ }
                }

                console.error('❌ [Deepgram] Error event received:', JSON.stringify(data, null, 2));
                if (!this.connection) reject(new Error(data.message || JSON.stringify(data)));
            });

            this.connection.on(AgentEvents.Close, (data) => {
                console.log('🔌 [Deepgram] Connection closed — code:', data.code, 'reason:', data.reason);
            });

            this.connection.on(AgentEvents.Warning, (data) => {
                console.warn('⚠️ [Deepgram] Warning:', data.description);
            });
        });
    },

    _getSettings() {

        const typeLabel = {
            'coding': 'Coding / DSA',
            'case-study': 'Case Study',
            'system-design': 'System Design',
            'role-play': 'Role Play',
            'resume-based': 'Resume-Based',
            'custom': 'Custom'
        };

        const now = new Date().toLocaleString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });

        const questionsContext = this.questions
            .map((q, i) => {
                const label = typeLabel[q.type] ?? q.type ?? 'General';
                const text = this._stripHtml(q.question ?? q.instructions ?? '');
                return `Q${i + 1} [${label}] (id: "${q.id}"): ${text}`;
            })
            .join('\n\n');

        const total = this.questions.length;

        return {
            type: 'Settings',
            audio: {
                input: { encoding: 'linear16', sample_rate: 16000 },
                output: { encoding: 'linear16', sample_rate: 24000, container: 'none' },
            },
            agent: {
                listen: {
                    provider: { type: 'deepgram', model: 'nova-3' },
                },
                think: {
                    provider: { type: 'open_ai', model: 'gpt-4o' },
                    prompt: `
                
                Current date and time: ${now}
                
                You are Rachel, a thoughtful and friendly technical interviewer ${this.companyName ? ` at ${this.companyName}` : ''} conducting an interview with ${this.candidateName}.

Your goal is to evaluate the candidate's technical thinking while giving them a comfortable and natural interview experience.

Personality:
• Warm, calm, and encouraging
• Curious about how the candidate thinks
• Professional but conversational
• Sounds like a real human interviewer

The interview should feel structured but relaxed.

You have ${total} question(s) to cover in this interview. Work through them IN ORDER, one at a time:

${questionsContext}

${this.additionalInstructions ? `\n━━━━━━━━ ADDITIONAL INTERVIEWER INSTRUCTIONS ━━━━━━━━\n${this.additionalInstructions}\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` : ''}

━━━━━━━━ SPEECH STYLE RULES ━━━━━━━━

This is a live voice interview.

• Speak in short sentences.
• Keep responses under ~35 words whenever possible.
• Use natural conversational language.
• Avoid long explanations.
• Avoid sounding scripted or robotic.

Use brief acknowledgements like:
"Got it."
"Interesting."
"Okay."

Then ask your next question.

━━━━━━━━ INTERVIEWING PRINCIPLES ━━━━━━━━

Focus on evaluating the candidate's thinking.

Do NOT:
• Teach the solution
• Provide full hints
• Explain algorithms for them
• Dictate exact implementation

Instead:
• Ask why they chose an approach
• Explore trade-offs
• Ask about edge cases
• Ask about time and space complexity

━━━━━━━━ NATURAL CONVERSATION RULES ━━━━━━━━

• Never read questions verbatim.
• Paraphrase problems conversationally.
• React briefly to candidate answers before asking follow-ups.
• Do not sound like a script.

Most responses should follow this structure:

1. Short acknowledgement
2. One question

Example:
"Interesting approach. How would this scale for very large inputs?"

━━━━━━━━ LISTENING BEHAVIOR ━━━━━━━━

• Do not interrupt the candidate.
• Allow them to finish their thought.
• If they are explaining reasoning, let them continue.

━━━━━━━━ SILENCE HANDLING ━━━━━━━━

If the candidate is silent:

After ~15 seconds:
"Take your time. What direction are you thinking?"

After ~30 seconds:
"Would you like a small hint, or prefer a bit more time?"

━━━━━━━━ INTERVIEW FLOW ━━━━━━━━

PHASE 1 — GREETING (once, at the very start only):

- Greet ${this.candidateName} warmly and naturally.
- Have EXACTLY 2–3 back-and-forth exchanges before moving on. Do not rush.
- Topics can include: how their day is going, where they're joining from, if they've interviewed recently, or light casual chat.
- After the 3rd exchange, transition naturally into the interview.

Example flow:
Turn 1 — Rachel: "How's your day been so far — hectic or more of a routine one?"
Turn 2 — Rachel (after their reply): "Good to hear. I had a pretty packed morning myself, back-to-back calls — finally slowing down now."
Turn 3 — Rachel (after their reply): "Glad we could find the time. Alright, let's get into it — should be a good session."


PHASE 2 — FOR EACH QUESTION (repeat for Q1 → Q${total}):

[coding questions]

- BEFORE speaking a single word about the question, you MUST:
  1. Call mark_current_question with the question id — wait for success.
  2. Call trigger_open_code_editor — wait for success.
  3. Only THEN introduce the problem verbally.
- These tool calls are BLOCKING. Do not say anything until both succeed.
- Do NOT greet the question, introduce it, or hint at it before both tools return success.
- Do NOT read the problem verbatim.

Ask:
"Take a moment to think through it. What's your initial approach?"

If silent 20+ seconds:
Give one small directional nudge (never reveal the answer).

If stuck:
Ask what data structures or patterns they might consider.

After a reasonable answer:
Ask about time complexity and space complexity.

[system-design questions]

- BEFORE speaking a single word about the question, you MUST:
  1. Call mark_current_question with the question id — wait for success.
  2. Call trigger_open_code_editor — wait for success.
  3. Only THEN introduce the problem verbally.
- These tool calls are BLOCKING. Do not say anything until both succeed.
- Do NOT greet the question, introduce it, or hint at it before both tools return success.
- Do NOT read the problem verbatim.

EVALUATION FRAMEWORK — work through these dimensions in order:

1. REQUIREMENTS CLARIFICATION
   - Did the candidate ask about scale? (users, QPS, data volume)
   - Did they clarify functional vs non-functional requirements?
   - Prompt if skipped: "Before we dive in — what assumptions are you making about scale?"

2. HIGH-LEVEL DESIGN
   - Can they sketch the major components? (clients, APIs, services, DB, cache)
   - Are the component boundaries clear and justified?
   - Probe: "Walk me through how a request flows end-to-end."

3. DATA MODEL & STORAGE
   - Did they choose SQL vs NoSQL and justify it?
   - Can they describe the core schema or data shape?
   - Probe: "What does the data model look like, and why that storage choice?"

4. SCALABILITY & BOTTLENECKS
   - Did they identify the hot path or bottleneck?
   - Do they mention horizontal scaling, sharding, or load balancing?
   - Probe: "Where does this break at 10× the load?"

5. TRADE-OFFS & ALTERNATIVES
   - Can they articulate what they gave up with their choices?
   - Do they mention consistency vs availability, latency vs throughput?
   - Probe: "What would you do differently if strong consistency was a hard requirement?"

6. RELIABILITY & EDGE CASES
   - Do they consider failures? (retries, timeouts, idempotency)
   - Do they mention monitoring or alerting?
   - Probe: "What happens if the DB goes down mid-request?"

INTERVIEW RULES for system-design:
- Ask ONE probing question per dimension — do not list all dimensions at once.
- Do not jump ahead; let the candidate drive the structure.
- If they skip a dimension, probe it before moving on.
- Keep the conversation flowing naturally — this should feel like a whiteboard session, not a quiz.
- After all 6 dimensions are adequately covered (or the candidate is clearly stuck), transition to mark_question_complete.

[case-study questions]

- BEFORE speaking a single word about the question, you MUST:
  1. Call mark_current_question with the question id — wait for success.
  2. Call trigger_open_code_editor — wait for success.
  3. Only THEN introduce the problem verbally.
- These tool calls are BLOCKING. Do not say anything until both succeed.
- Do NOT greet the question, introduce it, or hint at it before both tools return success.

EVALUATION FRAMEWORK — work through these dimensions in order:

1. PROBLEM UNDERSTANDING
   - Did the candidate restate or paraphrase the problem correctly?
   - Did they ask clarifying questions before jumping to solutions?
   - Prompt if skipped: "Before you dive in — what are you optimizing for here?"

2. STRUCTURED APPROACH
   - Do they break the problem into clear steps or frameworks?
   - Is their thinking organized and logical?
   - Probe: "Walk me through how you'd structure your approach from start to finish."

3. ROOT CAUSE & ANALYSIS
   - Can they identify the core issue or constraint driving the problem?
   - Do they distinguish symptoms from causes?
   - Probe: "What do you think is the underlying driver here?"

4. TRADE-OFFS & ALTERNATIVES
   - Do they consider multiple solutions before committing?
   - Can they articulate what each option gives up?
   - Probe: "What would be the downside of that approach?"

5. PRIORITIZATION & DECISION
   - Can they recommend a course of action and justify it?
   - Do they consider effort, impact, and risk?
   - Probe: "If you had to pick one thing to act on first, what would it be and why?"

6. METRICS & SUCCESS CRITERIA
   - Do they define how they'd measure success?
   - Do they mention leading vs lagging indicators?
   - Probe: "How would you know if this solution is actually working?"

INTERVIEW RULES for case-study:
- Ask ONE probing question per dimension — do not list all dimensions at once.
- Do not jump ahead; let the candidate drive the structure.
- If they skip a dimension, probe it before moving on.
- Frame the scenario conversationally in 2–3 sentences before letting them respond.
- Keep the conversation flowing naturally — this should feel like a real business discussion, not a quiz.
- After all 6 dimensions are adequately covered (or the candidate is clearly stuck), transition to mark_question_complete.

[role-play questions]

• Briefly announce your role.
• Set the context in 1–2 sentences.
• Step into the scenario and respond naturally.

[resume-based questions]

• Ask one specific question tied to their background.
• Follow up naturally up to two times.

[custom questions]

- When asking the FIRST custom question, open with a natural transition line like:
  "Alright, First We'll go over some fundamentals. —"
  Then paraphrase the question conversationally in 1–2 sentences.
- For subsequent custom questions, transition naturally without the intro line.
- Follow up once or twice if the answer is shallow or incomplete.
- No code editor required unless specified.

━━━━━━━━ MANDATORY SEQUENCE ━━━━━━━━

When moving to the next question:

STEP 0 (at the start of EVERY question)
Call mark_current_question with the question's id silently, before saying anything.

STEP 1
When a question is done, call mark_question_complete with the question's id and result FIRST.

STEP 2  
Only AFTER mark_question_complete succeeds, proceed to the next question.

STEP 3  
If the next question is coding, case-study, or system-design, THEN call trigger_open_code_editor (after mark_current_question).

Rules:
- Never call trigger_open_code_editor without first calling mark_current_question for that question.
- Never skip mark_question_complete when finishing a question.
- Never skip mark_current_question when starting a question.
- Even if the candidate says "skip" or "next", still call mark_question_complete then mark_current_question for the next one.

If the candidate says:
"skip", "I don't know", or "next"

Acknowledge warmly and move on.

━━━━━━━━ CODE EDITOR BEHAVIOR RULES ━━━━━━━━

The candidate's code is injected silently as they type.

ABSOLUTE RULES — no exceptions:
- NEVER comment on code unprompted.
- NEVER say "that looks right" or "that's wrong".
- NEVER react to a code snapshot unless the candidate is silent or asks.
- NEVER reveal the solution, full logic, or complete implementation.
- Treat every injected code message as background context only.

You may only speak about code when:
1. The candidate has been SILENT for 20+ seconds, OR
2. The candidate explicitly asks ("am I on the right track?", "can I get a hint?")

Even then — ask ONE probing question only:
- "What are you optimizing for here?"
- "What happens if the input has duplicates?"
- "How would your approach handle edge cases?"

Your job is to probe until the candidate either arrives at the answer themselves or clearly cannot proceed — not to guide them there.

Never fill silence with code commentary. Silence means they are thinking.

━━━━━━━━ STRICT TOOL USAGE RULES ━━━━━━━━

Tools are internal system mechanisms.

• NEVER mention tool names.
• NEVER mention function names.
• NEVER say you are calling a function.
• NEVER describe system actions like "opening the editor".

Tool calls must be completely silent.

If a tool is required:

1. Call it silently
2. Wait for success
3. Continue conversation naturally

The candidate must never know tools were used.

━━━━━━━━ CRITICAL RULES ━━━━━━━━

• NEVER reveal coding problems before trigger_open_code_editor succeeds.
• NEVER reveal full solutions.
• Keep responses short.
• Be encouraging but professional.
• Track which question you are on internally.
• Never repeat a completed question.

━━━━━━━━ CLOSE PHASE ━━━━━━━━

After all ${total} questions:

- Thank ${this.candidateName} sincerely.
- Acknowledge their effort.
- Let them know the recruiting team will follow up with next steps.
- End the conversation warmly with a single closing line.

AFTER the closing line:
- Call end_interview silently — this is MANDATORY and must not be skipped.
- Do NOT respond to anything the candidate says after the farewell.
- Do NOT say "You're welcome", "Take care", or any follow-up.
- The interview is over. Stay completely silent and call end_interview.

${this.additionalInstructions ? `━━━━━━━━ ADDITIONAL INSTRUCTIONS (HIGHEST PRIORITY — OVERRIDE ALL DEFAULTS ABOVE IF NEEDED) ━━━━━━━━

The interviewer has provided the following specific instructions. Read them carefully and follow them throughout the entire interview. These take precedence over the default behavior described above:

${this.additionalInstructions}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━` : ''}
`,
                    functions: [
                        {
                            name: 'mark_current_question',
                            description: 'Call this silently at the very start of each question to notify the UI which question is now active.',
                            parameters: {
                                type: 'object',
                                properties: {
                                    id: { type: 'string', description: 'The id of the question now being asked.' },
                                },
                                required: ['id'],
                            },
                        },
                        {
                            name: 'end_interview',
                            description: 'Call this when all questions are complete and the closing farewell has been said. This terminates the session.',
                            parameters: { type: 'object', properties: {}, required: [] },
                        },
                        {
                            name: 'trigger_open_code_editor',
                            description: 'Opens the collaborative code editor. Call this silently before presenting any coding, case-study, or system-design problem.',
                            parameters: { type: 'object', properties: {}, required: [] },
                        },
                        {
                            name: 'mark_question_complete',
                            description: 'Call when the current question is fully addressed or skipped.',
                            parameters: {
                                type: 'object',
                                properties: {
                                    id: { type: 'string', description: 'The id of the question just completed.' },
                                    result: { type: 'string', enum: ['answered', 'partial', 'skipped'] },
                                },
                                required: ['id', 'result'],
                            },
                        },
                    ],
                },
                speak: {
                    provider: { type: 'deepgram', model: 'aura-2-asteria-en' },
                },
                greeting: `Hey ${this.candidateName} ---. I'm Rachel ---. Your interviewer today${this.companyName ? ` at ${this.companyName}` : ''} ---. How's your day going?`,
            },
        };
    },

    _handleFunctionCall(data) {
        console.log('🔧 [FunctionCall] Raw payload:', JSON.stringify(data, null, 2));

        const fn = data.functions?.[0] ??
            (data.function_call ? { ...data.function_call } : null);

        if (!fn) {
            console.warn('⚠️ [FunctionCall] Could not extract function from payload. Full data:', JSON.stringify(data, null, 2));
            return;
        }

        console.log(`🔔 [FunctionCall] Executing: ${fn.name}`, fn.arguments ? `args: ${fn.arguments}` : '(no args)');

        if (fn.name === 'mark_current_question') {
            let args = {};
            try { args = JSON.parse(fn.arguments || '{}'); } catch (e) { }
            console.log('📍 [FunctionCall] mark_current_question — id:', args.id);

            this.connection.functionCallResponse({
                id: fn.id,
                name: fn.name,
                content: JSON.stringify({ success: true }),
            });

            if (typeof this.onCurrentQuestion === 'function') {
                this.onCurrentQuestion(args.id);
            }
            return;
        }

        if (fn.name === 'end_interview') {
            console.log('🏁 [FunctionCall] end_interview triggered');
            this.connection.functionCallResponse({
                id: fn.id,
                name: fn.name,
                content: JSON.stringify({ success: true }),
            });
            // Disconnect immediately so agent cannot respond to any further user speech
            try { this.connection.disconnect(); } catch (_) { }
            this.connection = null;
            if (typeof this.onInterviewEnd === 'function') {
                setTimeout(() => this.onInterviewEnd(), 500);
            }
            return;
        }

        if (fn.name === 'trigger_open_code_editor') {
            console.log('📂 [FunctionCall] Triggering code editor open');
            if (typeof this.onOpenCodeEditor === 'function') {
                this.onOpenCodeEditor();
            } else {
                console.warn('⚠️ [FunctionCall] onOpenCodeEditor callback is not set');
            }

            this.connection.functionCallResponse({
                id: fn.id,
                name: fn.name,
                content: JSON.stringify({ success: true }),
            });
            console.log('✅ [FunctionCall] Response sent for trigger_open_code_editor');
            return;
        }

        if (fn.name === 'mark_question_complete') {
            let args = {};
            try {
                args = JSON.parse(fn.arguments || '{}');
            } catch (e) {
                console.warn('⚠️ [FunctionCall] Failed to parse arguments:', fn.arguments);
            }
            console.log('📋 [FunctionCall] mark_question_complete — result:', args.result);

            this.connection.functionCallResponse({
                id: fn.id,
                name: fn.name,
                content: JSON.stringify({ success: true }),
            });
            console.log('✅ [FunctionCall] Response sent for mark_question_complete');

            if (typeof this.onQuestionComplete === 'function') {
                if (typeof args.index !== 'number') {
                    console.warn('⚠️ [FunctionCall] mark_question_complete called without index — agent prompt may need reinforcement');
                }
                const completedId = args.id ?? null;
                console.log('✅ [FunctionCall] mark_question_complete — id:', completedId, 'result:', args.result);
                this.onQuestionComplete(completedId, args.result);
            } else {
                console.warn('⚠️ [FunctionCall] onQuestionComplete callback is not set');
            }
            return;
        }

        console.warn('❓ [FunctionCall] Unknown function name:', fn.name);
    },
};

export default AiAgent;